/**
 * Traducción de errores de la API de Cargadores a la vista (ADR 0021).
 *
 * El backend responde con un contrato uniforme; `apiClient` ya lo normaliza a `ApiError`.
 * Acá se decide qué mostrar y a qué campo del formulario apuntar el error:
 * - Siempre se muestra el `message` del backend (fuente de verdad del texto).
 * - Si el error identifica el campo (`details.field` / `details.fieldName`), se marca ese.
 * - Si no, se cae al campo conocido por el `code` estable (ADR 0021: sin parsear mensajes).
 *
 * `EQUIPO_DATOS_INVALIDOS` (ChargePoint ID / protocolo / tipo de conector / powerType
 * inválido) no apunta a un campo único: se muestra como mensaje. `EQUIPO_NOT_FOUND` (404)
 * lo resuelve el hook de detalle.
 */
import { ApiError } from '@/lib/http/apiClient';

export interface StationApiError {
  /** Mensaje del backend, listo para mostrar. */
  message: string;
  /** Nombre del campo del formulario a marcar, si el error lo identifica. */
  field?: string;
}

/**
 * Códigos estables → campo del formulario (nombres del contrato/alta). El ChargePoint ID
 * duplicado, la locación inválida y los conectores son los errores que el backend asocia a
 * un campo concreto.
 *
 * ponytail: `EQUIPO_POTENCIA_INVALIDA` cubre también la potencia de un conector; sin
 * `details.field` se marca el campo del equipo (`maxPowerKw`). Si el backend manda el campo
 * del conector, `details.field` gana.
 */
const CODE_FIELD: Record<string, string> = {
  EQUIPO_CHARGEPOINT_DUPLICADO: 'id',
  EQUIPO_LOCACION_INVALIDA: 'locationId',
  EQUIPO_CONECTORES_REQUERIDOS: 'connectors',
  EQUIPO_CONECTOR_DUPLICADO: 'connectors',
  EQUIPO_POTENCIA_INVALIDA: 'maxPowerKw',
};

/** Extrae el campo de `details` (convención ADR 0021) si viene. */
function fieldFromDetails(details: unknown): string | undefined {
  if (typeof details !== 'object' || details === null) {
    return undefined;
  }
  const record = details as Record<string, unknown>;
  for (const key of ['field', 'fieldName'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }
  return undefined;
}

/**
 * Describe un error de cargador para mostrarlo en la página. `fallbackMessage` ajusta el
 * texto genérico según la acción (alta, edición, sync).
 */
export function describeStationApiError(
  error: unknown,
  fallbackMessage = 'No se pudo crear el cargador.',
): StationApiError {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      field: fieldFromDetails(error.details) ?? CODE_FIELD[error.code],
    };
  }
  if (error instanceof Error && error.message) {
    return { message: error.message };
  }
  return { message: fallbackMessage };
}
